import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import { dataPayload } from "./utils";
import * as api from "../api/adapters/json_api";

const type = "requisition_lists";
const path = `api_resources/${type}`;

/**
 * Returns the sort options if they are not empty.
 * @param {Object} sort - The sort settings.
 * @param {string} sort.field - The field to sort by.
 * @param {string} sort.direction - The direction of sorting ('asc' or 'desc').
 * @returns {string} - The sort options.
 */
const sortOptions = (sort) => {
  if (isEmpty(sort.field)) {
    return "-created_at";
  }
  const directionPrefix = sort.direction === "asc" ? "" : "-";
  return `${directionPrefix}${sort.field}`;
};

/**
 * Returns the search query if it is not empty.
 * @param {string} search - The search query.
 * @returns {null|Object} - The search options.
 */
const searchOptions = (search) => (isEmpty(search) ? null : { search });

export default {
  /**
   * FIN-1242: the Requisition Lists block of the Dashboard's Purchasing section,
   * GET /api_resources/requisition_lists/dashboard_purchasing. No vessels means every vessel
   * the user may see; 'all' for importance or type means no filter and is not sent.
   * @returns {Promise<Object>} the block, as the backend's `data`
   */
  dashboardPurchasing: ({ vesselIds, importance, type: rlType } = {}) => api
    .read({ type: path, id: "dashboard_purchasing" }, {
      query: {
        vessel_ids: vesselIds,
        importance: importance === "all" ? undefined : importance,
        type: rlType === "all" ? undefined : rlType,
      },
    })
    .then((response) => response.data.data),

  api,

  /**
   * Retrieves a specific requisition list.
   * @param {string|number} id - The ID of the requisition list to be retrieved.
   * @returns {Promise<*>} - The response from the API.
   */
  show: (id) => api.read({ type, id }, {}),

  /**
   * FIN-1213: sets the superintendent qty for a single RL line item.
   * PATCH /api_resources/requisition_lists/:id/items/:item_id
   * Passing the original (synced) qty rolls the edit back.
   * @param {string} id - requisition list id.
   * @param {string} itemId - the line item id (items[].id).
   * @param {number} superintendentQty - new qty (>= 0).
   * @returns {Promise<*>} - { item: { id, qty, original_qty, superintendent_qty, is_qty_modified } }
   */
  updateItem: (id, itemId, superintendentQty) => api.update(
    { type: path, id, action: `items/${itemId}` },
    {},
    { superintendent_qty: superintendentQty }
  ),

  /**
   * Retrieves a list of requisition lists.
   * @param {Object} pagination - The pagination settings.
   * @param {Object} sort - The sort settings.
   * @param {string} search - The search query.
   * @param {Object} filter - The filter settings.
   * @param {Object} settings - The settings.
   * @returns {Promise<*>} - The response from the API.
   */
  index: (pagination, sort, search, filter, settings) => {
    if (isEmpty(filter.vessel_imo)) {
      return Promise.resolve({ data: { data: [], included: [], meta: { page_count: 0 } } });
    }

    return api.read(
      { type: path },
      {
        query: {
          sort: sortOptions(sort),
          page: {
            size: pagination.itemsPerPage,
            number: pagination.activePage,
          },
          filter: {
            ...filter,
            ...searchOptions(search),
            issue_year: get(settings, "global.budgetYear"),
          },
        },
      }
    );
  },

  /**
   * Updates a requisition list.
   * @param {string|number} id - The ID of the requisition list to be updated.
   * @param {Object} attributes - The attributes to update.
   * @returns {Promise<*>} - The response from the API.
   */
  update: (id, attributes) => {
    const payload = dataPayload(type, attributes, id);
    return api.update({ type: path, id }, {}, payload);
  },

  /**
   * Creates a new request.
   * @param {Object} payload - The data to be sent in the request.
   * @returns {Promise<*>} - The response from the API.
   */
  createRequest: (payload) => api.create(
    { type: path, action: "create_requests" },
    {},
    payload
  ),

  sendQuotationReminder: (id) => (api.create(
    { type: path, action: "send_quotation_reminder" },
    {},
    { id }
  )),

  /**
   * Creates a new tender.
   * @param {Object} payload - The data to be sent in the request.
   * @returns {Promise<*>} - The response from the API.
   */
  createTender: (payload) => api.create(
    { type: path, action: "create_tender" },
    {},
    payload
  ),

  /**
   * FIN-1267: the warehouses the quotations of these lists name — for the tender made from a request.
   * @param {Array<string>} ids - The requisition list ids.
   * @returns {Promise<*>} - [{ id, name, quotations, suppliers }]
   */
  tenderWarehouses: (ids) => api.read(
    { type: path, action: "tender_warehouses" },
    { query: { ids } }
  ),

  /**
   * FIN-1267: the warehouse a tender is filtered by, if any.
   * @param {string} tenderId - The ID of the tender.
   * @returns {Promise<*>} - { warehouse: { id, name } | null }
   */
  tenderWarehouse: (tenderId) => api.read(
    { type: path, action: "tender_warehouse" },
    { query: { tender_id: tenderId } }
  ),

  /**
   * FIN-1267: takes the warehouse filter off a tender — the quotations of every warehouse come back.
   * @param {string} tenderId - The ID of the tender.
   * @returns {Promise<*>} - { warehouse: null }
   */
  clearTenderWarehouse: (tenderId) => api.create(
    { type: path, action: "clear_tender_warehouse" },
    {},
    { tender_id: tenderId }
  ),

  /**
   * Creates a new tender item.
   * @param {Object} payload - The data to be sent in the request.
   * @returns {Promise<*>} - The response from the API.
   */
  createTenderItem: (payload) => api.create(
    { type: path, action: "create_tender_item" },
    {},
    payload
  ),

  /**
   * Creates multiple tender items at once.
   * @param {Object} payload - The data to be sent in the request.
   * @returns {Promise<*>} - The response from the API.
   */
  bulkCreateTenderItems: (payload) => api.create(
    { type: path, action: "bulk_create_tender_items" },
    {},
    payload
  ),

  /**
   * Updates a specific tender item.
   * @param {string|number} id - The ID of the tender item to be updated.
   * @param {Object} payload - The data to be sent in the request.
   * @returns {Promise<*>} - The response from the API.
   */
  updateTenderItem: (id, payload) => api.create(
    { type: path, action: "update_tender_item" },
    {},
    { id, ...payload }
  ),

  /**
   * Deletes a specific tender item.
   * @param {string|number} id - The ID of the tender item to be deleted.
   * @returns {Promise<*>} - The response from the API.
   */
  deleteTenderItem: (id) => api.create(
    { type: path, action: "delete_tender_item" },
    {},
    { id }
  ),

  /**
   * Retrieves the tender table for a specific tender.
   * @param {string|number} tenderId - The ID of the tender.
   * @returns {Promise<*>} - The response from the API.
   */
  tenderTable: (tenderId) => api.read(
    { type: path, action: "tender_table" },
    { query: { tender_id: tenderId } }
  ),

  /**
   * Generates a purchase order.
   * @param {Object} payload - The data to be sent in the request.
   * @returns {Promise<*>} - The response from the API.
   */
  generatePO: (payload) => api.create(
    { type: path, action: "generate_po" },
    {},
    payload
  ),

  /**
   * Adds items to an existing purchase order.
   * @param {Object} payload - The data to be sent in the request.
   * @param {string} payload.purchaseOrderId - The ID of the purchase order.
   * @param {Array} payload.data - The items to add.
   * @returns {Promise<*>} - The response from the API.
   */
  addToExistingPO: (payload) => api.create(
    { type: path, action: "add_to_existing_po" },
    {},
    payload
  ),

  /**
   * Retrieves purchase orders compatible with the given criteria.
   * @param {Object} params - Query parameters.
   * @param {string} params.supplierId - The ID of the supplier.
   * @param {string} params.currency - The currency.
   * @param {string} params.vesselId - The ID of the vessel.
   * @returns {Promise<*>} - The response from the API.
   */
  getCompatiblePOs: (params) => api.read(
    { type: path, action: "compatible_purchase_orders" },
    { query: params }
  ),

  /**
   * Removes a supplier.
   * @param {Object} payload - The data to be sent in the request.
   * @returns {Promise<*>} - The response from the API.
   */
  removeSupplier: (payload) => api.create(
    { type: path, action: "remove_supplier" },
    {},
    payload
  ),

  /**
   * Cancels a requisition list.
   * @param {string|number} id - The ID of the requisition list to be canceled.
   * @returns {Promise<*>} - The response from the API.
   */
  cancel: (id) => api.create(
    { type: path, action: "cancel", id },
    {},
    {}
  ),

  /**
   * Adds a comment to a requisition list.
   * @param {string|number} id - The ID of the requisition list.
   * @param {Object} payload - The comment data.
   * @returns {Promise<*>} - The response from the API.
   */
  addComment: (id, payload) => api.create(
    { type: path, action: "add_comment", id },
    {},
    payload
  ),

  /**
   * Batch load multiple requisition lists by IDs.
   * @param {Array<string>} ids - Array of requisition list IDs.
   * @returns {Promise<*>} - The response from the API.
   */
  batchLoad: (ids) => {
    if (!ids || ids.length === 0) {
      return Promise.resolve({ data: { data: [] } });
    }
    return api.read(
      { type: path },
      {
        query: {
          filter: { id: ids.join(',') },
          page: { size: ids.length, number: 1 },
        },
      }
    );
  },

  /**
   * Fetches estimated prices for RL items based on historical purchase/quotation data.
   * @param {string|number} id - The ID of the requisition list.
   * @returns {Promise<*>} - The response from the API with items and totals.
   */
  orderEstimate: (id) => api.read(
    { type: path, action: "order_estimate" },
    { query: { id } }
  ),

  /**
   * Fetches the purchase history of every article of an RL (FIN-1214).
   * @param {string|number} id - The ID of the requisition list.
   * @returns {Promise<*>} - { data: { "<article_id>": [{ date, port, price_usd, supplier, quantity, unit }] } }
   */
  priceHistory: (id) => api.read(
    { type: path, action: "price_history" },
    { query: { id } }
  ),

  /**
   * Fetches Remaining-on-Board (ROB) values from Shipvisor for the items of an RL.
   * @param {string|number} id - The ID of the requisition list.
   * @returns {Promise<*>} - { data: [{ article_id, rob, by_state: [{state, qty}, ...] }] }
   */
  rob: (id) => api.read(
    { type: path, id, action: "rob" },
    {}
  ),
};
